import React, { useEffect, useState } from "react"
import { Paper, Typography } from "@mui/material"
import { MRT_Cell, MRT_PaginationState } from "material-react-table";
import { fetchColumnSummary, fetchStatistics, generateStatistics } from "../../modules/datasetModule.ts";
import { columnSummaryPayload } from "../../modules/types/Dataset.model.ts";
import ReusableTable from "../../components/common/Table.tsx";
import SyncIcon from "@mui/icons-material/Sync";
import Button from "../../components/common/Button.tsx";
import SummaryModal from "./SummaryModal.tsx";
import { useSSE } from "../../context/SSEContext.tsx";
import { ToastContainer } from "react-toastify";

interface StatisticsProps {
    id: string;
    toast: any;
}

export interface chartData {
    series: number[] | null,
    labels: string[] | null,
    xaxis: string[] | null
}

export interface statisticsData {
    column_name: string,
    null_count: number,
    null_percentage: number,
    unique_count: number,
    unique_percentage: number,
    min_value: string,
    max_value: string,
    mean: number,
    mode: string,
}

export interface columnSummary {
    chart_type: string,
    chart_data: chartData
    statistics_data: statisticsData
}

const StatisticTable: React.FC<StatisticsProps> = ({ id, toast }) => {
    const [statisticsData, setStatisticsData] = useState<any[]>([]);
    const [statisticsColumn, setStatisticsColumn] = useState<any[]>([]);
    const [tableError, setTableError] = useState<string | null>(null);
    const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
    const [endTime, setEndTime] = useState<string | null>(null);
    const [statisticsResult, setStatisticsResult] = useState<any>(null);
    const [open, setOpen] = useState<boolean>(false)
    const [tableLoading, setTableLoading] = useState<boolean>(true);
    const [isDisabled, setIsDisabled] = useState<boolean>(false)
    const [columnData, setColumnData] = useState<columnSummary | null>(null)
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 5 });
    const { subscribeJob } = useSSE();

    useEffect(() => {
        getStatistics();
    }, []);

    const fetchSummary = async (columnName) => {
        if (!columnName) {
            return;
        }
        try {
            const payload: columnSummaryPayload = {
                dataset_id: parseInt(id),
                column_name: columnName
            }
            const data: columnSummary = await fetchColumnSummary(payload);
            setColumnData(data)
        } catch (error: any) {
            console.error("Error generating column summary:", error);
            toast.error(error.message || "Failed to generate column summary");
        }
    }

    const handleGenerateStatistics = async () => {
        setIsDisabled(true)
        try {
            const resp = await generateStatistics(id);
            if (resp?.data?.use_ray)
                subscribeJob("statistics", parseInt(id));
            getStatistics();
        } catch (err: any) {
            toast.error(err.message || "Failed to generate dataset statistics");
        }
        setIsDisabled(false)
    };

    const getStatistics = async () => {
        if (!id) return setTableError("No dataset ID provided");
        try {
            const data = await fetchStatistics(id);
            setStatisticsResult(data)
            const columns = data?.status === "Completed"
                ? [
                    {
                        accessorKey: "column_name",
                        header: "Columns",
                        Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
                            const value = cell.getValue<string>();
                            return (
                                <span
                                    className="text-blue-600 hover:text-blue-800 cursor-pointer"
                                    onClick={() => {
                                        setSelectedColumn(value);
                                        fetchSummary(value);
                                        setOpen(true);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setSelectedColumn(value)
                                            setOpen(true);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {value}
                                </span>
                            );
                        },
                    },
                    { accessorFn: (row) => `${row?.unique_count ?? "-"}`, header: "Unique Values" },
                    { accessorFn: (row) => `${row?.unique_percentage ?? "-"}`, header: "% Unique Values" },
                    { accessorFn: (row) => `${row?.null_count ?? "-"}`, header: "Null Values" },
                    { accessorFn: (row) => `${row?.null_percentage ?? "-"}`, header: "% Null Values" }
                ]
                : [];

            const rows = data?.status === "Completed" ? data.statistics_result : [];
            if (data?.status === "Completed" && data.end_time) {
                const d = new Date(data.end_time);
                const date = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear().toString().slice(-2)}`;
                const time = `${(d.getHours() % 12 || 12)}:${d.getMinutes().toString().padStart(2, '0')} ${d.getHours() >= 12 ? 'PM' : 'AM'}`;
                setEndTime(`${date}, ${time}`);
            }
            setStatisticsColumn(columns);
            setStatisticsData(rows);
        } catch (err: any) {
            const msg = err.message || "Failed to load dataset statistics";
            console.error(msg);
            setTableError("Failed to load dataset statistics");
            toast.error(msg);
        } finally {
            setTableLoading(false);
        }
    };

    return (
        <>
            <ToastContainer position="top-center" />
            <h5 className="rv-page-title p-2">Statistics</h5>
            <div className="flex flex-col sm:flex-row justify-content-end items-start sm:items-center gap-4">
                <div className="flex flex-col-reverse lg:flex-row gap-3">
                    {(statisticsResult?.status === "Completed" && endTime) && <p className="mt-2 font-medium">Statistics has been generated on {endTime}</p>}
                    {statisticsResult?.status === "Failed" && <p className="mt-2 font-medium" >Failed to generate statistics</p>}
                    {statisticsResult?.status === "Completed" && <div className="flex flex-row gap-1 sm:self-end">
                        <button
                            onClick={handleGenerateStatistics}
                            type="submit"
                            disabled={statisticsResult?.status === "In Progress" || isDisabled}
                            className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary mb-6"
                        >
                            <i className="ri-bar-chart-2-fill label-icon align-middle fs-16 me-1 "></i>
                            <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                                Generate Statistics
                            </span>
                        </button>
                        {statisticsResult?.status === "In Progress" && <SyncIcon className="mt-2 cursor-pointer" onClick={getStatistics} />}
                    </div>}
                    {(statisticsResult?.status !== "Completed" && statisticsData.length === 0) && <div className="flex flex-row gap-1 sm:self-end">
                        <button
                            onClick={handleGenerateStatistics}
                            type="submit"
                            disabled={statisticsResult?.status === "In Progress"}
                            className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary mb-6"
                        >
                            <i className="ri-bar-chart-2-fill label-icon align-middle fs-16 me-1 "></i>
                            <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                                Generate Statistics
                            </span>
                        </button>
                        {statisticsResult?.status === "In Progress" && <SyncIcon className="mt-2 cursor-pointer" onClick={getStatistics} />}
                    </div>}
                </div>
            </div>

            {/* Moved container-fluid here - after the title and buttons */}
            <div className="container-fluid px-0">
                <div className="disable-margin-card table-responsive table-customer border table-border w-100">
                    <ReusableTable
                        columns={statisticsColumn}
                        data={statisticsData}
                        error={tableError}
                        enableSorting={false}
                        enableColumnFilters={false}
                        enablePagination
                        pagination={pagination}
                        onPaginationChange={setPagination}
                        renderEmptyRowsFallback={() => {
                            return (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        // alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        height: "100%",
                                        width: "100%",
                                        color: "GrayText",
                                        // margin: "25px 0px"
                                    }}
                                >
                                    {statisticsResult?.status !== "In Progress" ? (
                                        <>
                                            <span className="font-medium mt-0 pt-0">
                                                Click Generate Statistics to see the statistics of the dataset
                                            </span>
                                            {/* <div className="mt-3">
                                                <button
                                                    onClick={handleGenerateStatistics}
                                                    type="submit"
                                                    disabled={isDisabled}
                                                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary mb-6"
                                                >
                                                    <i className="ri-bar-chart-2-fill label-icon align-middle fs-16 me-1 "></i>
                                                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                                                        Generate Statistics
                                                    </span>
                                                </button>
                                            </div> */}
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-medium">
                                                Generation of the statistics is in progress...
                                            </span>
                                            {/* <div className="mt-3">
                                                <button
                                                    onClick={getStatistics}
                                                    type="submit"
                                                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary mb-6"
                                                >
                                                    <i className="ri-refresh-line label-icon align-middle fs-16 me-1 "></i>
                                                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                                                        Refresh
                                                    </span>
                                                </button>
                                            </div> */}
                                        </>
                                    )}
                                </div>
                            );
                        }}
                    />
                </div>
            </div>
            <SummaryModal open={open} setOpen={setOpen} selectedColumn={selectedColumn} setSelectedColumn={setSelectedColumn} columnData={columnData} setColumnData={setColumnData} />
        </>
        // <>
        //     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        //         <div className="flex flex-col-reverse lg:flex-row gap-3">
        //             {(statisticsResult?.status === "Completed" && endTime) && <p className="mt-2 font-medium">Statistics has been generated on {endTime}</p>}
        //             {statisticsResult?.status === "Failed" && <p className="mt-2 font-medium" >Failed to generate statistics</p>}
        //             {statisticsResult?.status === "Completed" && <div className="flex flex-row gap-1 sm:self-end">
        //                 <Button
        //                     className="w-full sm:w-auto px-8 py-2 text-white rounded-lg"
        //                     onClick={handleGenerateStatistics}
        //                     disabled={statisticsResult?.status === "In Progress" || isDisabled}
        //                 >
        //                     Generate Statistics
        //                 </Button>
        //                 {statisticsResult?.status === "In Progress" && <SyncIcon className="mt-2 cursor-pointer" onClick={getStatistics} />}
        //             </div>}
        //         </div>
        //     </div>
        //     <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        //         <Typography fontWeight="bold" mb={2}>Statistics</Typography>
        //         <ReusableTable
        //             columns={statisticsColumn}
        //             data={statisticsData}
        //             error={tableError}
        //             enableSorting={false}
        //             enableColumnFilters={false}
        //             enablePagination
        //             pagination={pagination}
        //             onPaginationChange={setPagination}
        //             renderEmptyRowsFallback={() => {
        //                 return (
        //                     <div
        //                         style={{
        //                             display: "flex",
        //                             flexDirection: "column",
        //                             alignItems: "center",
        //                             justifyContent: "center",
        //                             textAlign: "center",
        //                             height: "100%", 
        //                             width: "100%",
        //                             color: "GrayText",
        //                             margin: "25px 0px"
        //                         }}
        //                     >
        //                         {statisticsResult?.status !== "In Progress" ? (
        //                             <>
        //                                 <span className="font-medium">
        //                                     Click Generate to see the statistics of the dataset
        //                                 </span>
        //                                 <div className="mt-3">
        //                                     <Button onClick={handleGenerateStatistics} disabled={isDisabled}>
        //                                         Generate
        //                                     </Button>
        //                                 </div>
        //                             </>
        //                         ) : (
        //                             <>
        //                                 <span className="font-medium">
        //                                     Generation of the statistics is in progress...
        //                                 </span>
        //                                 <div className="mt-3">
        //                                     <Button onClick={getStatistics}>Refresh</Button>
        //                                 </div>
        //                             </>
        //                         )}
        //                     </div>
        //                 );
        //             }}
        //         />
        //     </Paper>
        //     <SummaryModal open={open} setOpen={setOpen} selectedColumn={selectedColumn} setSelectedColumn={setSelectedColumn} columnData={columnData} setColumnData={setColumnData} />
        // </>
    )
}

export default StatisticTable;